import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";
import axiosInstance from "../../api/axiosInstance";
import { useTokenFromURL } from "../../hooks/useTokenFromURL";

interface User {
  user_id: number;
  username: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  logout: () => void;
  checkTokenExpiry: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  setUser: () => {},
  logout: () => {},
  checkTokenExpiry: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Capture token from URL if passed via iframe
  // Token is set as HTTP-only cookie via backend endpoint
  useTokenFromURL({ removeFromUrl: true });

  const logout = useCallback(() => {
    setUser(null);
    // Remove accessToken from localStorage
    localStorage.removeItem("accessToken");
    console.log("[AuthContext] AccessToken removed from localStorage");
    
    // Only redirect if not already on auth pages
    const isAuthPage =
      window.location.pathname.includes("/sign-up") ||
      window.location.pathname.includes("/log-in") ||
      window.location.pathname.includes("/2fa") ||
      window.location.pathname.includes("/activate");

    if (!isAuthPage) {
      window.location.href = "/log-in";
    }
  }, []);

  const checkTokenExpiry = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/auth/me", {
        withCredentials: true,
      });

      if (response.data && response.data.user) {
        setUser(response.data.user);
      } else {
        logout();
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        logout();
      }
    }
  }, [logout]);

  const initialAuthCheck = useCallback(async () => {
    // Check if we're on an authentication page
    const isAuthPage =
      window.location.pathname.includes("/sign-up") ||
      window.location.pathname.includes("/log-in") ||
      window.location.pathname.includes("/2fa") ||
      window.location.pathname.includes("/activate");

    // Skip auth check on authentication pages
    if (isAuthPage) {
      setLoading(false);
      return;
    }

    // If accessToken exists in localStorage, set a temporary user to prevent redirect
    // The JWT cookie will be validated by API calls, and 401 will trigger logout
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      console.log("[AuthContext] AccessToken exists - setting temporary user and skipping /me check");
      // Set a temporary user object so routes don't redirect to login
      setUser({ user_id: 0, username: "verifying" });
      setLoading(false);
      return;
    }

    // No accessToken - do normal auth check
    try {
      const response = await axiosInstance.get("/api/auth/me", {
        withCredentials: true,
      });

      if (response.data && response.data.user) {
        // console.log("User in AuthProvider:", response.data.user);
        setUser(response.data.user);
      } else {
        setUser(null);
      }
    } catch (error: any) {
      // console.log(
      //   "Auth check error (expected for non-logged in users):",
      //   error.message
      // );
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initialAuthCheck();
  }, [initialAuthCheck]);

  // Listen for token verification from URL
  useEffect(() => {
    const handleTokenVerified = (event: CustomEvent<{ user: User }>) => {
      console.log("Token verified from URL. User:", event.detail.user);
      setUser(event.detail.user);
      setLoading(false);
    };

    window.addEventListener("tokenVerified", handleTokenVerified as EventListener);
    return () => window.removeEventListener("tokenVerified", handleTokenVerified as EventListener);
  }, []);

  // Set up periodic token validation (every 5 minutes)
  useEffect(() => {
    if (!user) return; // Only check if user is logged in

    const interval = setInterval(() => {
      checkTokenExpiry();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [user, checkTokenExpiry]);

  // Check token when tab becomes active (handles cases where user leaves tab open)
  useEffect(() => {
    const handleFocus = () => {
      if (user) {
        checkTokenExpiry();
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [user, checkTokenExpiry]);

  // Listen for visibility change (when user switches tabs)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        checkTokenExpiry();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [user, checkTokenExpiry]);

  return (
    <AuthContext.Provider
      value={{ user, loading, setUser, logout, checkTokenExpiry }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
